import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { MessageResponse } from '@voll-intelligence/domain';
import {
  isForeignCustomer,
  isOutboundTranslationOn,
  MessageAuthorSchema,
} from '@voll-intelligence/domain';
import { ChatUiMessageSchema, ChatUiSchema, type ChatUi } from '@voll-intelligence/genui-contract';
import {
  ChatUiView,
  GenUiConfigProvider,
  slotOf,
  type GenUiDispatch,
} from '@voll-intelligence/genui-kit/react';
import { queryKeys } from '@web/core/query';
import { conversationRepository } from '@web/data/repositories';
import {
  messagesToThread,
  dedupById,
  type ThreadMessage,
} from '@web/data/repositories/conversation/messages_mapper';
import { currentLocale, t } from '@web/i18n';
import { AppIcons, Button, Input, Switch } from '@web/ui/core/atoms';
import { EmptyHint, ToneChip } from '@web/ui/core/molecules';
import { useSnackbar } from '@web/ui/core/molecules/snackbar';
import { fireAndForget, twcn } from '@web/ui/core/utils';

import { MessageMarkdown } from './message_markdown';
import {
  useKeepScrollOnPrepend,
  useOlderMessages,
  withOlder,
  type OlderMessages,
} from './use_older_messages';

// Distance from the bottom (px) within which the operator counts as "pinned" to
// the latest message — new arrivals keep them pinned; scrolled-up means we leave
// the viewport alone. A scroll threshold (behaviour), not a design dimension.
const PINNED_BOTTOM_PX = 80;

// Stand-in until the first page lands: nothing to show and no older page to ask
// for. Keeps `ConversationThread` under its complexity cap (no `?.` per field).
const NO_THREAD: { messages: ThreadMessage[]; nextCursor: string | null } = {
  messages: [],
  nextCursor: null,
};

type ThreadPage = { data: MessageResponse[]; nextCursor: string | null };

// Merges live SSE pushes into the raw cache page, deduped by messageId.
function mergeIncoming(prev: ThreadPage | undefined, incoming: MessageResponse[]): ThreadPage {
  const base = prev ?? { data: [], nextCursor: null };
  const byId = new Map(base.data.map((m) => [m.messageId, m]));
  for (const m of incoming) byId.set(m.messageId, m);
  return { ...base, data: [...byId.values()] };
}

// Header row shows once there's something to show: the customer's language, or
// the Zendesk desk badge. Kept out of `ConversationThread` to stay under its
// branching cap.
function showsHeaderRow(
  conversationId: string | undefined,
  customerLanguage: string | undefined,
  zendeskDesk: boolean,
): boolean {
  return Boolean(conversationId) && (Boolean(customerLanguage) || zendeskDesk);
}

// A Zendesk-bridged conversation is read-only for the operator even once
// assumed — the Zendesk agent, not the console, replies to the traveler.
function showsComposer(canReply: boolean, zendeskDesk: boolean): boolean {
  return canReply && !zendeskDesk;
}

export interface ConversationThreadProps {
  conversationId?: string;
  banner?: React.ReactNode;
  /** Show the reply composer. Consumers gate this on the current user being the
   * card's assignee (`assignedToMe`) — replying requires assuming the card. */
  canReply: boolean;
}

/**
 * Shared conversation panel — chat thread + reply composer for a card. Reads messages
 * from the conversation service (`GET /conversations/:id/messages`) and receives new
 * messages live via the conversation SSE stream. Consumed by the card drawer and the
 * triage detail panel.
 */
export function ConversationThread({
  conversationId,
  banner,
  canReply,
}: Readonly<ConversationThreadProps>): React.JSX.Element {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  const activeConversationId = conversationId ?? '';

  const metaQuery = useQuery({
    queryKey: queryKeys.conversation.meta(activeConversationId),
    queryFn: () => conversationRepository.loadMeta(activeConversationId),
    enabled: Boolean(conversationId),
  });
  const customerLanguage = metaQuery.data?.customerLanguage;
  // Desk bridge: a Zendesk agent serves this traveler — the operator only watches.
  const zendeskDesk = metaQuery.data?.humanDesk === 'zendesk';
  // Outbound translation is opt-OUT for foreign customers (on unless turned off).
  // The toggle is meaningless for a same-language (PT) customer, so only show it
  // when the customer is foreign; its checked state follows the shared rule.
  const showTranslationToggle = isForeignCustomer(customerLanguage);
  const translationOn = isOutboundTranslationOn(metaQuery.data?.translationMode, customerLanguage);

  const translationModeMutation = useMutation({
    mutationFn: (mode: 'off' | 'on') =>
      conversationRepository.setTranslationMode(activeConversationId, mode),
    onSuccess: () => {
      fireAndForget(
        queryClient.invalidateQueries({
          queryKey: queryKeys.conversation.meta(activeConversationId),
        }),
      );
    },
    onError: () => {
      showSnackbar({ message: t('card.drawer.conversation.translationError'), severity: 'error' });
    },
  });

  const threadQuery = useQuery({
    queryKey: queryKeys.conversation.thread(activeConversationId),
    queryFn: () =>
      conversationRepository.loadMessages(activeConversationId, { order: 'desc', limit: 50 }),
    enabled: Boolean(conversationId),
    select: (page) => ({
      messages: dedupById(messagesToThread(page.data)),
      nextCursor: page.nextCursor,
    }),
  });

  const thread = threadQuery.data ?? NO_THREAD;
  const older = useOlderMessages(activeConversationId, thread.nextCursor);
  const messages = withOlder(older.messages, thread.messages);
  const scrollRef = useRef<HTMLDivElement>(null);
  const keepScrollOnPrepend = useKeepScrollOnPrepend(scrollRef, older.messages.length);

  // SSE: subscribe to live message pushes for the open conversation. Each push
  // merges into the raw cache page (dedup by messageId); `select` re-derives the
  // thread so the component re-renders automatically.
  useEffect(() => {
    if (!conversationId) return undefined;
    return conversationRepository.subscribeThread(conversationId, (incoming) => {
      queryClient.setQueryData(
        queryKeys.conversation.thread(conversationId),
        (prev: ThreadPage | undefined) => mergeIncoming(prev, incoming),
      );
    });
  }, [conversationId, queryClient]);

  // Keep the newest message in view as the thread grows (initial load + live SSE
  // pushes), but only while the operator is already at the bottom — don't yank
  // them up while they scroll back through history. Imperative DOM scroll synced
  // to the last message id, so useEffect is the right tool here.
  const bottomRef = useRef<HTMLDivElement>(null);
  const pinnedToBottomRef = useRef(true);
  const lastMessageId = messages.at(-1)?.id;

  useEffect(() => {
    if (lastMessageId && pinnedToBottomRef.current) {
      bottomRef.current?.scrollIntoView({ block: 'end' });
    }
  }, [lastMessageId]);

  function handleThreadScroll(e: React.UIEvent<HTMLDivElement>): void {
    const el = e.currentTarget;
    pinnedToBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < PINNED_BOTTOM_PX;
  }

  if (conversationId && threadQuery.isLoading) {
    return (
      <div className="flex-1 overflow-auto px-5 py-6">
        <p className="text-meta text-muted-foreground">{t('card.drawer.conversation.loading')}</p>
      </div>
    );
  }
  if (conversationId && threadQuery.isError) {
    return (
      <div className="flex-1 overflow-auto px-5 py-6">
        <p className="text-meta text-tone-red-fg">{t('board.drawer.errorLoading')}</p>
      </div>
    );
  }

  return (
    // genui-kit cards render their intro (`note`) via the host's markdown renderer —
    // reuse the thread's MessageMarkdown so card text matches the chat bubbles.
    <GenUiConfigProvider
      config={{
        locale: currentLocale(),
        renderMarkdown: (text) => <MessageMarkdown text={text} />,
      }}
    >
      <div data-testid="conversation-thread" className="flex min-h-0 flex-1 flex-col">
        {showsHeaderRow(conversationId, customerLanguage, zendeskDesk) ? (
          <ConversationHeaderRow
            zendeskDesk={zendeskDesk}
            customerLanguage={customerLanguage}
            showTranslationToggle={showTranslationToggle}
            translationOn={translationOn}
            translationPending={translationModeMutation.isPending}
            onTranslationModeChange={(mode) => {
              translationModeMutation.mutate(mode);
            }}
          />
        ) : null}
        <div
          ref={scrollRef}
          onScroll={handleThreadScroll}
          className="flex-1 overflow-auto px-5 py-6"
        >
          {messages.length === 0 ? (
            <EmptyHint testId="conversation-empty">{t('card.drawer.conversation.empty')}</EmptyHint>
          ) : (
            <div className="flex flex-col gap-3">
              {older.hasMore ? (
                <LoadOlder older={older} onBeforeLoad={keepScrollOnPrepend} />
              ) : null}
              <div className="self-center font-mono text-tiny uppercase tracking-eyebrow text-faint-foreground">
                {t('card.drawer.conversation.today')}
              </div>
              {messages.map((m) => (
                <MessageRow key={m.id} message={m} />
              ))}
              <div ref={bottomRef} />
            </div>
          )}
        </div>

        {banner}
        {showsComposer(canReply, zendeskDesk) ? (
          <div className="shrink-0 border-t border-border-soft px-5 py-3">
            <Composer conversationId={conversationId} />
          </div>
        ) : null}
      </div>
    </GenUiConfigProvider>
  );
}

interface ConversationHeaderRowProps {
  zendeskDesk: boolean;
  customerLanguage: string | undefined;
  showTranslationToggle: boolean;
  translationOn: boolean;
  translationPending: boolean;
  onTranslationModeChange: (mode: 'off' | 'on') => void;
}

// Chip row above the thread: Zendesk desk badge, customer-language chip, and the
// outbound-translation toggle. Split out of `ConversationThread` to keep that
// function's branching under the complexity cap.
function ConversationHeaderRow({
  zendeskDesk,
  customerLanguage,
  showTranslationToggle,
  translationOn,
  translationPending,
  onTranslationModeChange,
}: Readonly<ConversationHeaderRowProps>): React.JSX.Element {
  return (
    <div className="flex shrink-0 items-center gap-2 border-b border-border-soft px-5 py-2">
      {zendeskDesk ? (
        <ToneChip tone="brand">{t('card.drawer.conversation.zendeskDesk')}</ToneChip>
      ) : null}
      {customerLanguage ? <ToneChip tone="gray">{customerLanguage.toUpperCase()}</ToneChip> : null}
      {showTranslationToggle ? (
        <label className="flex items-center gap-2 text-meta text-muted-foreground">
          <Switch
            checked={translationOn}
            onCheckedChange={(checked) => {
              onTranslationModeChange(checked ? 'on' : 'off');
            }}
            disabled={translationPending}
            aria-label={t('card.drawer.conversation.traduzirConversa')}
          />
          {t('card.drawer.conversation.traduzirConversa')}
        </label>
      ) : null}
    </div>
  );
}

function LoadOlder({
  older,
  onBeforeLoad,
}: Readonly<{ older: OlderMessages; onBeforeLoad: () => void }>): React.JSX.Element {
  return (
    <div className="flex flex-col items-center gap-1 self-center">
      {older.failed ? (
        <p className="m-0 text-meta text-tone-red-fg">
          {t('card.drawer.conversation.loadOlderError')}
        </p>
      ) : null}
      <Button
        variant="ghost"
        size="sm"
        disabled={older.loading}
        onClick={() => {
          onBeforeLoad();
          older.loadOlder();
        }}
      >
        {older.loading
          ? t('card.drawer.conversation.loadingOlder')
          : t('card.drawer.conversation.loadOlder')}
      </Button>
    </div>
  );
}

function MessageBody({ message }: Readonly<{ message: ThreadMessage }>): React.JSX.Element {
  const [showOriginal, setShowOriginal] = useState(false);
  if (!message.translation) return <MessageMarkdown text={message.text} />;
  return (
    <>
      <MessageMarkdown text={message.translation.text} />
      <button
        type="button"
        className="mt-1 text-meta text-muted-foreground underline"
        onClick={() => {
          setShowOriginal((v) => !v);
        }}
      >
        {t('card.drawer.conversation.verOriginal')}
      </button>
      {showOriginal ? (
        <div className="mt-1 text-meta opacity-80">
          <MessageMarkdown text={message.text} />
        </div>
      ) : null}
      <span className="mt-1 block text-meta text-faint-foreground">
        {message.translation.engine === 'operator-original'
          ? t('card.drawer.conversation.enviadoTraduzido')
          : t('card.drawer.conversation.traduzidoAuto')}
      </span>
    </>
  );
}

// The operator console is unconditionally read-only: it never triggers a card's
// action (reply / interaction / booking-checkout). Cards hide their CTA under
// `readOnly`; this NOOP guarantees nothing leaks even if one were rendered.
const NOOP_DISPATCH: GenUiDispatch = () => {};

// The renderable chat-slot card for a message, if any. `ui` travels opaque on the
// wire (`payload.ui`); genui-kit owns `ChatUiMessageSchema`, so we validate at the
// render boundary — an invalid/absent list degrades to plain text, never throws.
function chatCardOf(message: ThreadMessage): ChatUi | undefined {
  if (message.author === MessageAuthorSchema.enum.customer) return undefined;
  return uiBlocksOf(message.ui).find((b) => slotOf(b.kind) === 'chat');
}

// Per-block salvage, mirroring the wire's other two consumers (web-chat
// `ui/thread/blocks.ts`, app-integration `shared/parse_ui_block.ts`): kinds are
// added continuously, and a stale bundle receiving a NEWER kind must drop only
// the block it cannot read — failing the whole list here degraded the message to
// plain text and the operator lost the KNOWN cards of the turn too. The salvaged
// list still passes through `ChatUiMessageSchema` so the list-level invariants
// (block cap, id uniqueness) keep holding for what survives.
function uiBlocksOf(ui: unknown): ChatUi[] {
  const whole = ChatUiMessageSchema.safeParse(ui);
  if (whole.success) return whole.data;

  const items: unknown[] = Array.isArray(ui) ? ui : [];
  const blocks = items.flatMap((item) => {
    const block = ChatUiSchema.safeParse(item);
    return block.success ? [block.data] : [];
  });
  const parsed = ChatUiMessageSchema.safeParse(blocks);
  return parsed.success ? parsed.data : [];
}

function MessageRow({ message }: Readonly<{ message: ThreadMessage }>): React.JSX.Element {
  // Support side (agent, operator, system) sits on the right; the customer on the left.
  const isRight = message.author !== MessageAuthorSchema.enum.customer;
  // `agent` (AI) and `system` (automated platform dispatch) share the assistant visual.
  const isAgentSide =
    message.author === MessageAuthorSchema.enum.agent ||
    message.author === MessageAuthorSchema.enum.system;
  const time = message.at.toLocaleTimeString(currentLocale(), {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  });
  let label: string;
  if (message.author === MessageAuthorSchema.enum.customer) {
    label = t('card.drawer.conversation.customerName');
  } else if (message.author === MessageAuthorSchema.enum.operator) {
    label = t('card.drawer.conversation.operatorName');
  } else if (message.author === MessageAuthorSchema.enum.system) {
    label = t('card.drawer.conversation.systemName');
  } else {
    label = t('card.drawer.conversation.aiName');
  }

  // A fala do agente vem acima do card, como o cliente a vê: nem todo card a carrega.
  const card = chatCardOf(message);

  return (
    <div
      data-message-id={message.id}
      className={twcn('flex flex-col gap-0.5', isRight ? 'items-end' : 'items-start')}
    >
      <span className="inline-flex items-center gap-1 text-meta text-muted-foreground">
        {isAgentSide ? <AppIcons name="sparkle" size={12} /> : null}
        {label}
      </span>
      {!card || message.text ? (
        <div
          className={twcn(
            'w-fit max-w-full rounded-md px-3 py-2 text-base text-foreground',
            message.author === MessageAuthorSchema.enum.customer && 'bg-subtle',
            isAgentSide && 'bg-muted',
            message.author === MessageAuthorSchema.enum.operator && 'bg-primary-tint',
          )}
        >
          <MessageBody message={message} />
        </div>
      ) : null}
      {card ? (
        // Full width, never fit-content: genui-kit card roots declare
        // `container-type: inline-size` (zero intrinsic width), so a shrink-to-fit
        // wrapper collapses the card to its narrowest container-query layout. The
        // mount hands the card the whole thread column — the card caps its own
        // width — mirroring web-chat's `.assist-card { width: 100% }` contract.
        <div data-testid="genui-card-mount" className="w-full">
          {/* `replay`: the console only remounts persisted history, never the
              customer's live emission — a turn gesture (`hotel_planner`
              `directives.open`) would open a fullscreen takeover over the page. */}
          <ChatUiView ui={card} readOnly replay dispatch={NOOP_DISPATCH} />
        </div>
      ) : null}
      <span className="font-mono text-card-id text-faint-foreground tabular-nums">{time}</span>
    </div>
  );
}

function Composer({ conversationId }: Readonly<{ conversationId?: string }>): React.JSX.Element {
  const [text, setText] = useState('');
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  const activeConversationId = conversationId ?? '';

  const replyMutation = useMutation({
    mutationFn: (message: string) => {
      const messageId = crypto.randomUUID();
      return conversationRepository.sendReply(activeConversationId, { text: message, messageId });
    },
    onSuccess: () => {
      setText('');
      // SSE echo is the primary update path; invalidate as a fallback to ensure
      // the thread is up to date if the stream hasn't delivered the echo yet.
      fireAndForget(
        queryClient.invalidateQueries({
          queryKey: queryKeys.conversation.thread(activeConversationId),
        }),
      );
    },
    onError: () => {
      showSnackbar({ message: t('card.drawer.composer.error'), severity: 'error' });
    },
  });

  const trimmed = text.trim();
  const canSend = Boolean(conversationId) && trimmed.length > 0 && !replyMutation.isPending;

  function submit(): void {
    if (!canSend) return;
    replyMutation.mutate(trimmed);
  }

  return (
    <form
      className="flex items-center gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <Input
        value={text}
        onChange={(e) => {
          setText(e.target.value);
        }}
        disabled={!conversationId || replyMutation.isPending}
        placeholder={t('card.drawer.composer.placeholder')}
        aria-label={t('card.drawer.composer.placeholder')}
      />
      <Button
        type="submit"
        variant="primary"
        size="sm"
        disabled={!canSend}
        aria-disabled={!canSend}
      >
        {t('card.drawer.composer.send')}
      </Button>
    </form>
  );
}
